// ─────────────────────────────────────────────────────────────────────────────
// REQ-124 — Vista de equipo del líder (/app/equipo).
//
// Consume GET v1/analytics/recognition/team (TeamMetricsDto). El backend resuelve el equipo del
// manager del token (un AdminGlobal puede consultar otro manager). NUNCA muestra puntos (regla de
// oro): solo conteos de participación y estado de racha legible. Estados honestos (loading/error/
// empty) reales de TanStack Query. Gateo UX: AdminOU/AdminGlobal siempre entran; un manager raso
// entra a su propio equipo (`useManagerStatus`, Task 15/17) — en ambos casos la autoridad real la
// valida el backend en cada llamada a /team.
//
// RF-043 (S4) — La columna "Racha" está sujeta al interruptor de Gestión igual que el resto del
// módulo. Es la única superficie de racha que NO sale de `useMyStreak` (los datos vienen de
// `useRecognitionTeam`, por persona), así que el grep del hook no la encuentra: si el flag se apaga
// y esta columna se queda, un líder sigue viendo "activa / en riesgo" de cada colaborador y el RF
// ("que no aparezca") queda incumplido en esta pantalla.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import {
  useRecognitionTeam, useManagerStatus, useRecognitionCatalogForReporting,
  getFundamentalName, getFundamentalColor,
} from '@/api/recognition';
import type { TeamMemberMetricDto, PersonSummary } from '@/api/recognition';
import { useEffectiveRoles, useHasRecognitionPermission } from '@/auth/RoleGuard';
import { useUserContext } from '@/contexts/UserContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PersonAvatar } from '../postitShared';
import { Pager } from '@/components/common/Pager';
import { RecognitionShell, RecognitionHeader, StateBlock } from '../components';
import { useStreakEnabled } from '../useVisibleStreak';
import { useVistaHabilitada } from '../useVistas';
import { MetricTiles } from './metricsShared';

/** Fundamental ya resuelto contra el catálogo: nombre para pintar y color de su etiqueta. */
type FundTag = { nombre: string; color: string | null };

/** Color del Fundamental como variable CSS de la etiqueta (el CSS cae a un gris si no viene). */
function fundStyle(fund: FundTag): CSSProperties | undefined {
  return fund.color ? ({ '--fund-color': fund.color } as CSSProperties) : undefined;
}

/** Estado de racha → clase de color (sin puntos). */
function rachaClass(estado: string | null | undefined): string {
  const e = (estado ?? '').toLowerCase();
  if (e.includes('riesgo') || e.includes('risk')) return 'met-racha--risk';
  if (e) return 'met-racha--on';
  return 'met-racha--none';
}

/**
 * Fila de un miembro del equipo. El nombre expande el detalle (enviados/recibidos/racha y el botón
 * «explorar su equipo», RN-EQ01, cascada jerárquica); "Ver" abre el modo "perfil de compañero" ya
 * existente en `PerfilView`. Desde el Anti-Spoiler por campaña (2026-09-23) no se gatea: el servidor
 * quita del muro ajeno lo protegido.
 */
function MemberRow({
  m, colorIndex, streakEnabled, fundRecibido, fundEnviado, onView, onExplorar,
}: Readonly<{
  m: TeamMemberMetricDto;
  /** Posición de la persona en la tabla del equipo: el color del avatar sigue esa secuencia. */
  colorIndex: number;
  streakEnabled: boolean;
  /** Por lo que MÁS LO RECONOCEN. Es el dato que tiene valor para todo el equipo. */
  fundRecibido: FundTag | null;
  /** Lo que ÉL más usa al reconocer. Vacío para quien recibe pero no envía. */
  fundEnviado: FundTag | null;
  onView: (member: TeamMemberMetricDto) => void;
  /** RN-EQ01 — baja al equipo de esta persona. */
  onExplorar: () => void;
}>) {
  const { t } = useTranslation('recognition');
  const [expanded, setExpanded] = useState(false);
  // `colSpan` sigue al número REAL de columnas: Miembro + Enviados + Recibidos + (Racha?) +
  // Top Fundamental + Acciones. Un colSpan fijo dejaría la fila de detalle desbordando la tabla.
  const cols = streakEnabled ? 6 : 5;
  const streakLabel = t('team.colStreak');
  return (
    <>
    <tr className={`met-member-row ${expanded ? 'met-member-row--expanded' : ''}`} onClick={() => setExpanded((value) => !value)}>
      <td className="met-cell-member">
        <button
          type="button"
          className="met-expand-btn"
          aria-expanded={expanded}
          aria-label={t('team.expandMember', { name: m.nombre })}
        >
          <i className={`fas fa-chevron-${expanded ? 'down' : 'right'}`} aria-hidden="true" />
          <PersonAvatar name={m.nombre} photoUrl={null} baseClass="met-member-av" colorIndex={colorIndex} />
        </button>
        <span className="met-member-name">{m.nombre}</span>
      </td>
      <td className="met-col-num" data-label={t('team.colSent')}>{m.enviados}</td>
      <td className="met-col-num" data-label={t('team.colReceived')}>{m.recibidos}</td>
      {streakEnabled && (
        <td data-label={streakLabel}>
          {m.rachaEstado
            ? <span className={`met-racha ${rachaClass(m.rachaEstado)}`}>{m.rachaEstado}</span>
            : <span className="rr-muted">{t('team.noStreak')}</span>}
        </td>
      )}
      {/*
        * Una sola columna con las DOS lecturas, etiquetadas. Antes enseñaba un solo Fundamental sin
        * decir cuál de los dos era, y encima era el de «lo que envía»: en un equipo que recibe mucho
        * y envía poco, la columna entera salía en «—» (reporte de UAT). El recibido va primero y sin
        * condición —es el que tiene valor para todo el equipo—; el enviado solo si existe, para no
        * llenar la tabla de renglones vacíos.
        */}
      <td data-label={t('team.colTopFundamental')}>
        <div className="met-fund-cell">
          <span className="met-fund-line">
            <span className="met-fund-role">{t('team.fundRecibido')}</span>
            {fundRecibido
              ? <span className="met-fund-tag" style={fundStyle(fundRecibido)}>{fundRecibido.nombre}</span>
              : <span className="rr-muted">—</span>}
          </span>
          {fundEnviado && (
            <span className="met-fund-line met-fund-line--secundaria">
              <span className="met-fund-role">{t('team.fundEnviado')}</span>
              <span className="met-fund-tag" style={fundStyle(fundEnviado)}>{fundEnviado.nombre}</span>
            </span>
          )}
        </div>
      </td>
      <td data-label={t('team.colActions')}>
        <button
          type="button"
          className="met-view-btn"
          aria-label={t('team.viewButton', { name: m.nombre })}
          onClick={(event) => { event.stopPropagation(); onView(m); }}
        >
          <i className="fas fa-eye" aria-hidden="true" /> {t('team.viewButton_short')}
        </button>
      </td>
    </tr>
    {expanded && (
      <tr className="met-member-detail">
        <td colSpan={cols}>
          <div><strong>{t('team.sent')}:</strong> {m.enviados}</div>
          <div><strong>{t('team.received')}:</strong> {m.recibidos}</div>
          {streakEnabled && (
            <div><strong>{streakLabel}:</strong> {m.rachaEstado || t('team.noStreak')}</div>
          )}
          {/*
            * RN-EQ01 — «explorar más». Solo para quien tiene reportes directos: `m.isManager` ya
            * lo calcula el backend (REQ-086, GetDirectReportsQueryHandler cruzando ManagerId en
            * Mongo), el mismo campo que gatea el botón "Equipo" del Perfil (PerfilView.tsx). Sin
            * este filtro, cada hoja del organigrama mostraba un botón que solo llevaba a un
            * "equipo vacío".
            */}
          {m.isManager && (
            <button
              type="button"
              className="btn btn--ghost met-top-gap"
              onClick={(e) => { e.stopPropagation(); onExplorar(); }}
            >
              <i className="fas fa-sitemap" aria-hidden="true" /> {t('team.explore')}
            </button>
          )}
        </td>
      </tr>
    )}
    </>
  );
}

// FASE 3 — exportado para que AdminView.tsx lo embeba como tab ("Equipo"), ya que /app/equipo
// quedó sin punto de entrada al eliminar la nav horizontal (RecognitionNav). Ver CONTRATO-RUTAS.md §7.
/**
 * RN-EQ01 — «explorar más». El requisito pide cascada jerárquica completa: «la interacción por
 * default muestra primero los reportes directos, con opción de "explorar más" para expandir hacia
 * niveles inferiores solo si el usuario lo requiere».
 *
 * El backend ya lo permite (AS #20: un líder puede pedir el equipo de quien cuelga de él, y solo de
 * quien cuelga de él — RN-EQ04). Lo que faltaba era la puerta en la pantalla.
 *
 * La ruta se guarda como PILA, no como un solo id: sin ella, «volver» solo podría llevar a la raíz, y
 * quien bajó tres niveles tendría que empezar de cero. Cada nivel es un `managerId` distinto y el
 * hook los cachea por separado, así que subir es instantáneo.
 *
 * NO se comprueba aquí quién puede bajar: la autoridad es el backend, que responde 403 si el equipo
 * pedido no cuelga del solicitante. Duplicar esa regla en el front la pondría en dos sitios y con dos
 * criterios — el defecto que este módulo ya pagó con el gate por rol.
 */
export function TeamContent() {
  const { t, i18n } = useTranslation('recognition');
  const navigate = useNavigate();
  // Pila de descenso: cada entrada es {id, nombre} del líder cuyo equipo se está viendo. Vacía = mi
  // propio equipo, que es el default que pide RN-EQ01.
  const [ruta, setRuta] = useState<Array<{ id: string; nombre: string }>>([]);
  const actual = ruta.at(-1);

  const q = useRecognitionTeam(actual ? { managerId: actual.id } : {});
  const streakEnabled = useStreakEnabled();
  // 🔴 Catálogo de REPORTERÍA (incluye los dados de baja), no el de creación. `topFundamentalId` es
  // un dato HISTÓRICO: si el admin retiró ese Fundamental del catálogo después, el `find` de
  // `fundNameOf` falla y la columna «Top Fundamental» cae al «—» aunque la persona sí tenga
  // actividad — que es como se reportó en UAT («Top Fundamental no aparece»). Es la misma razón por
  // la que existe `getFundamentalsIncludingInactive` en `api/client.ts`.
  const catalog = useRecognitionCatalogForReporting();
  const data = q.data;
  const isEmpty = !!data && data.teamSize === 0 && data.miembros.length === 0;
  const lang = i18n.language;

  /**
   * Resuelve un id de Fundamental contra el catálogo en UNA sola búsqueda (antes había dos por
   * etiqueta — una para el nombre y otra para el color —, y ahora se pintan hasta dos etiquetas por
   * fila). Devuelve `null` si el id no está en el catálogo: la celda enseña «—», nunca un id crudo.
   */
  function fundTagOf(fundamentalId: string | null | undefined): FundTag | null {
    if (!fundamentalId) return null;
    const index = catalog?.data?.findIndex((item) => item.id === fundamentalId) ?? -1;
    if (index < 0) return null;
    const fund = catalog!.data![index];
    return { nombre: getFundamentalName(fund, lang), color: getFundamentalColor(fund, index) };
  }

  function viewMember(member: TeamMemberMetricDto) {
    const person: PersonSummary = {
      id: member.userId, email: '', displayName: member.nombre,
      jobTitle: null, ouId: null, ouName: null, photoUrl: null,
    };
    navigate('/app/perfil', { state: { colleague: person } });
  }

  return (
    <>
      {/* Con la pila vacía (mi propio equipo) el regreso natural es al perfil; al bajar niveles, el
          breadcrumb de abajo asume la navegación de vuelta. */}
      {ruta.length === 0 && (
        <Link to="/app/perfil" className="met-back-link">
          <i className="fas fa-chevron-left" aria-hidden="true" /> {t('team.backToProfile')}
        </Link>
      )}

      <RecognitionHeader
        title={t('team.title')}
        subtitle={data ? t('team.subtitleFor', { name: data.managerName }) : t('team.subtitle')}
      />

      {/*
        * Las migas van FUERA del StateBlock a propósito. Dentro, bajar a alguien SIN equipo activa el
        * estado vacío y se lleva por delante la navegación: el usuario queda sin forma de volver, en
        * una pantalla que no le dice ni dónde está. Lo cazó la prueba de dos niveles.
        */}
      {ruta.length > 0 && (
        <nav className="met-breadcrumb" aria-label={t('team.breadcrumb')}>
          <button type="button" className="btn btn--ghost" onClick={() => setRuta([])}>
            {t('team.myTeam')}
          </button>
          {ruta.map((paso, i) => (
            <span key={paso.id}>
              {' › '}
              <button
                type="button"
                className="btn btn--ghost"
                // Volver corta la pila HASTA ese punto, no la vacía: quien bajó tres niveles
                // vuelve al segundo sin perder el camino.
                onClick={() => setRuta((r) => r.slice(0, i + 1))}
              >
                {paso.nombre}
              </button>
            </span>
          ))}
        </nav>
      )}

      <StateBlock
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={isEmpty}
        emptyText={t('team.empty')}
        onRetry={() => q.refetch()}
      >
        {data && (
          <div className="met-wrap met-wrap--team">
            {data.suprimido && (
              <div className="rr-suppressed-note">
                <i className="fas fa-user-shield" aria-hidden="true" /> {t('reports.common.suppressedNote')}
              </div>
            )}

            <MetricTiles
              className="met-tile--team"
              items={[
                { label: t('team.teamSize'), value: data.teamSize, color: '#342F82' },
                { label: t('team.sent'), value: data.enviadosEquipo, color: '#F08521' },
                { label: t('team.received'), value: data.recibidosEquipo, color: '#20B15A' },
                { label: t('team.participation'), value: `${Math.round(data.participacionPct)}%`, color: '#40B8E9' },
              ]}
            />


            <h2 className="met-section-title">{t('team.members')}</h2>
            <div className="met-top-grid">
              <TopList
                titulo={t('team.mostRecognized')}
                miembros={data.miembros}
                valorDe={(m) => m.recibidos}
              />
              <TopList
                titulo={t('team.mostRecognizers')}
                miembros={data.miembros}
                valorDe={(m) => m.enviados}
              />
            </div>
            {data.miembros.length === 0 ? (
              <p className="rr-muted">{t('team.emptyMembers')}</p>
            ) : (
              <div className="rr-table-wrap">
                <table className="rr-table met-table--cards-mobile">
                  <thead>
                    <tr>
                      <th>{t('team.colMember')}</th>
                      <th className="met-col-num">{t('team.colSent')}</th>
                      <th className="met-col-num">{t('team.colReceived')}</th>
                      {streakEnabled && <th>{t('team.colStreak')}</th>}
                      <th>{t('team.colTopFundamental')}</th>
                      <th>{t('team.colActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.miembros.map((m, indice) => (
                      <MemberRow
                        key={m.userId}
                        m={m}
                        colorIndex={indice}
                        streakEnabled={streakEnabled}
                        fundRecibido={fundTagOf(m.topFundamentalId)}
                        fundEnviado={fundTagOf(m.topFundamentalEnviadoId)}
                        onView={viewMember}
                        onExplorar={() => setRuta((r) => [...r, { id: m.userId, nombre: m.nombre }])}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </StateBlock>
    </>
  );
}

/**
 * RN-EQ03 — «El listado de "Top reconocedores/reconocidos" … requiere paginación para líderes con
 * equipos grandes (30+ personas a cargo). Este listado se muestra SIEMPRE paginado,
 * independientemente del tamaño del equipo.»
 *
 * Antes era un `.slice(0, 3)`: con 40 personas a cargo, el líder veía tres y no había forma de
 * llegar a la cuarta. Truncar no es paginar — la información que falta ni siquiera se anuncia.
 *
 * «Siempre paginado» se toma al pie de la letra: el pie se pinta aunque quepa todo en una página,
 * porque su otra función es decir CUÁNTOS hay. Un equipo de dos también merece saber que son dos.
 */
function TopList({
  titulo, miembros, valorDe,
}: {
  titulo: string;
  miembros: TeamMemberMetricDto[];
  valorDe: (m: TeamMemberMetricDto) => number;
}) {
  const { t } = useTranslation('recognition');
  const [pagina, setPagina] = useState(1);
  const [tam, setTam] = useState(5);

  // Desempate por `userId`, y no es cosmético: con 40 personas la mayoría comparte el mismo conteo
  // —muchos ceros—, y un orden que solo compara el valor deja el resto al orden de llegada del API.
  // Al paginar eso significa que alguien puede salir en dos páginas y otro en ninguna. Es el mismo
  // razonamiento con el que `CampaignOuWinnerIndex` desempata por Id en el backend.
  const ordenados = useMemo(
    () => miembros.slice().sort((a, b) => valorDe(b) - valorDe(a) || a.userId.localeCompare(b.userId)),
    [miembros, valorDe],
  );

  const totalPaginas = Math.max(1, Math.ceil(ordenados.length / tam));
  // Si el equipo encoge (o cambia el filtro) la página actual puede quedar fuera de rango: se acota
  // al render en vez de dejar la lista en blanco sin explicación.
  const actual = Math.min(pagina, totalPaginas);
  const desde = (actual - 1) * tam;
  const visibles = ordenados.slice(desde, desde + tam);

  return (
    <section className="met-top-card">
      <h3>{titulo}</h3>
      {visibles.map((m, i) => (
        <div key={m.userId} className="met-top-row">
          {/* La numeración es ABSOLUTA, no relativa a la página: en la segunda página el primero es
              el 6.º del equipo, y reiniciarla en 1 haría creer que hay dos primeros puestos. */}
          <span>{desde + i + 1}. {m.nombre}</span>
          <strong>{valorDe(m)}</strong>
        </div>
      ))}
      {ordenados.length === 0 && <p className="rr-muted">{t('team.emptyMembers')}</p>}
      {/* El `Pager` común, el mismo de las tablas del admin: trae el rango, el salto a página y el
          selector de tamaño ya resueltos y traducidos. Escribir aquí otro paginador sería una
          segunda forma de hacer lo mismo, que es como acaban divergiendo. */}
      <Pager
        total={ordenados.length}
        page={actual}
        pageSize={tam}
        onPage={setPagina}
        onPageSize={(nuevo) => { setTam(nuevo); setPagina(1); }}
      />
    </section>
  );
}

export function TeamView() {
  const { t } = useTranslation('recognition');
  const { demoUser } = useUserContext();
  const roles = useEffectiveRoles();
  const isAdminRole = roles.some((r) => r === 'AdminOU' || r === 'AdminGlobal');
  const managerStatus = useManagerStatus(demoUser.userId || undefined);
  // REQ-124 — `ModuloMetricasEquipo` está declarado en el backend con NoRoleRestriction: autoriza
  // por permiso `viewAnalytics` + alcance Team, sin mirar el rol NI si la persona tiene reportes
  // directos en AAD (p. ej. un analista con el permiso otorgado a mano). Es una vía de acceso
  // independiente y no sustituye a `useManagerStatus`: alguien puede ser manager real en AAD sin
  // tener este permiso explícito, o tener el permiso sin ser manager. Se combinan con OR.
  const currentUser = useCurrentUser();
  const hasViewAnalyticsPermission = useHasRecognitionPermission('viewAnalytics');
  // 2026-08-20 — el toggle «Ver a tu equipo» de Admin › Vistas (WP-F3/CPE-158) vuelve a componerse
  // aquí: es el mismo gate que el botón «Mi equipo» de PerfilView.tsx, así que entrar por URL
  // directa a /app/equipo no debe saltárselo. Solo afecta la vía de manager raso — AdminOU/
  // AdminGlobal y quien ya tiene `viewAnalytics` siguen entrando sin depender de este interruptor.
  const verEquipo = useVistaHabilitada('verATuEquipo');
  // AdminOU/AdminGlobal siempre entran (backend: scope OuBu+); un manager raso entra a SU PROPIO
  // equipo (backend: capacidad ModuloMetricasEquipo, scope Team) — este gate es solo UX, la
  // autoridad real ya la valida el backend en cada llamada a /team.
  const isAllowed = isAdminRole || hasViewAnalyticsPermission
    || (managerStatus.data?.isManager === true && verEquipo);
  // Mientras el perfil (permisos) o `useManagerStatus` siguen en vuelo, y ninguna vía de acceso ya
  // se confirmó, todavía no se sabe si hay acceso: pintar "denegado" en ese hueco sería un falso
  // negativo momentáneo, y encima un lector de pantalla lo anuncia (`role="alert"`). Solo se
  // resuelve a loading/denegado una vez que ambas consultas terminaron, o de entrada si alguna vía
  // ya autoriza sin depender de las demás.
  const profileResolving = !currentUser || !currentUser.profileResolved;
  const isResolving = !isAdminRole && !hasViewAnalyticsPermission
    && (managerStatus.isLoading || profileResolving);

  if (isResolving) {
    return (
      <RecognitionShell>
        <p role="status" className="rec-state rec-state--loading">{t('common.loading')}</p>
      </RecognitionShell>
    );
  }

  // Un manager raso ante un error transitorio de `useManagerStatus` (retry: 0, p. ej. un 5xx) NO
  // debe caer en "denegado": `isLoading` ya pasó a `false` y `data` queda `undefined`, así que sin
  // esta rama `isAllowed` sería `false` aunque el backend lo habría autorizado. AdminOU/AdminGlobal
  // y quien ya tiene el permiso `viewAnalytics` no dependen de esta consulta, así que solo aplica al
  // resto. Mismo patrón visual que `StateBlock` usa para sus estados de error (`components.tsx`).
  if (!isAdminRole && !hasViewAnalyticsPermission && managerStatus.isError) {
    return (
      <RecognitionShell>
        <div role="alert" className="rec-state rec-state--error">
          <p className="rec-state-text">{t('common.error')}</p>
          <button type="button" className="rec-state-retry" onClick={() => managerStatus.refetch()}>
            {t('common.retry')}
          </button>
        </div>
      </RecognitionShell>
    );
  }

  if (!isAllowed) {
    return (
      <RecognitionShell>
        <div className="adm-denied" role="alert"><i className="fas fa-lock" /> {t('admin.denied')}</div>
      </RecognitionShell>
    );
  }

  return (
    <RecognitionShell>
      <TeamContent />
    </RecognitionShell>
  );
}

export default TeamView;
